import {
  type Rule,
  SpaceMemberRoles,
  type SpaceMembership
} from '@shared/all/domains/spaces';
import { type RouterOutputs } from '@shared/all/domains/trpc';
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';

import { useUserIdentity } from '../context/UserContext';
import { useRouteName } from '../helpers/navigation';
import { type WrappedTRPCClient } from '../helpers/trpcClientTypes';

export type SpaceType = RouterOutputs['spaces']['get'];

interface UseSpaceOptions {
  client?: WrappedTRPCClient;
  identifier?: string | null;
}

const useSpace = (options?: UseSpaceOptions | WrappedTRPCClient) => {
  // Support both old (client only) and new (options object) signatures
  let client: WrappedTRPCClient | undefined;
  let identifier: string | null | undefined;

  if (
    options &&
    typeof options === 'object' &&
    ('client' in options || 'identifier' in options)
  ) {
    // New options object format
    const opts = options;
    client = opts.client;
    identifier = opts.identifier;
  } else {
    // Old format: options is the client directly
    client = options as WrappedTRPCClient | undefined;
    identifier = undefined;
  }

  const queryClient = useQueryClient();
  const routeName = useRouteName();
  const currentUser = useUserIdentity();

  // Use provided identifier, fallback to route name
  const spaceIdentifier = identifier !== undefined ? identifier : routeName;

  const queryKey = ['space', spaceIdentifier, currentUser.username];

  // Do not use queryClient here, it will cause a hydration error
  const isOwner = (space: SpaceType) =>
    space.membership?.roles.includes(SpaceMemberRoles.OWNER) || false;
  const isModerator = (space: SpaceType) =>
    (space.membership?.roles.includes(SpaceMemberRoles.MODERATOR) &&
      !space.banned) ||
    isOwner(space);
  const isMember = (space: SpaceType) =>
    ((space.membership?.roles.includes(SpaceMemberRoles.MEMBER) ||
      isOwner(space) ||
      isModerator(space)) &&
      !space.banned) ||
    false;

  const updateSpace = (space: SpaceType) => {
    queryClient.setQueryData(queryKey, space);
  };

  const pinSpaceFactiii = async (factiii: {
    id: number;
    name: string;
    slug: string;
    spaceSlug: string | undefined;
    avatarKey: string | null;
    requirements: string[];
  }) => {
    if (!client) {
      console.error('Client not provided to useSpace hook');
      return [new Error('Client not provided'), undefined];
    }

    if (!factiii.spaceSlug) {
      console.error('spaceSlug is required for pinSpaceFactiii');
      return [new Error('spaceSlug is required'), undefined];
    }

    const [error, result] = await client.factiiis.pin.mutate({
      type: 'SPACE',
      factiiiId: factiii.id,
      spaceSlug: factiii.spaceSlug
    });
    if (error) return [error, undefined];
    const factiiiToPin = {
      id: factiii.id,
      name: factiii.name,
      slug: factiii.slug,
      spaceSlug: factiii.spaceSlug, // We know it exists because we checked above
      avatarKey: factiii.avatarKey,
      requirements: factiii.requirements
    };
    queryClient.setQueryData(queryKey, (old: SpaceType) => {
      if (!old) return alert('No space found');
      return {
        ...old,
        pinnedFactiiis:
          result.status === 'PINNED'
            ? [...(old.pinnedFactiiis || []), factiiiToPin]
            : (old.pinnedFactiiis || []).filter((f) => f.id !== factiiiToPin.id)
      };
    });
    return [undefined, result];
  };

  const updateSpaceSlug = (slug: string) => {
    queryClient.setQueryData(queryKey, (old: SpaceType) => {
      if (!old) return alert('No space found');
      return {
        ...old,
        slug
      };
    });
  };

  const addRule = (rule: Rule) => {
    queryClient.setQueryData(queryKey, (old: SpaceType) => {
      if (!old) return alert('No space found');
      return {
        ...old,
        rules: [...old.rules, rule]
      };
    });
  };

  const editRule = (rule: Rule) => {
    queryClient.setQueryData(queryKey, (old: SpaceType) => {
      if (!old) return alert('No space found');
      const rules = [...old.rules];
      const ruleIndex = rules.findIndex((r) => r.id === rule.id);
      if (ruleIndex < 0) return old;
      rules[ruleIndex] = rule;
      return {
        ...old,
        rules
      };
    });
  };

  const joinSpaceUpdate = (spaceSlug: string, data: SpaceMembership) => {
    queryClient.invalidateQueries({
      queryKey: ['space-membership', spaceSlug] //invalidate queries starting with space-membership
    });
    queryClient.setQueryData(queryKey, (old: SpaceType) => {
      if (!old) return alert('No space found');
      // INVITED users are no longer included in membersCount (server filters
      // them out), so accepting always increments by 1.
      return {
        ...old,
        membership: {
          roles: data.roles,
          expires: data.expires,
          createdAt: data.createdAt
        },
        membersCount: old.membersCount + 1
      };
    });
  };

  const leaveSpaceUpdate = (spaceSlug: string) => {
    queryClient.invalidateQueries({
      queryKey: ['space-membership', spaceSlug] //invalidate queries starting with space-membership
    });
    queryClient.setQueryData(queryKey, (old: SpaceType) => {
      if (!old) return alert('No space found');
      return {
        ...old,
        membership: undefined,
        membersCount: old.membersCount - 1
      };
    });
  };

  const filterChangeHandler = async (
    spaceSlug: string,
    type: 'add' | 'remove'
  ) => {
    if (!client) {
      console.error('Client not provided to useSpace hook');
      return { success: false, error: 'Client not provided' };
    }

    if (type === 'add') {
      const [error] = await client.spaces.addFilter.mutate({ slug: spaceSlug });
      if (error) return { success: false, error };
      await queryClient.invalidateQueries({
        queryKey: ['users', 'spaceFilters']
      });
      return { success: true, type: 'added' };
    } else {
      const [error] = await client.spaces.removeFilter.mutate({
        slug: spaceSlug
      });
      if (error) return { success: false, error };
      await queryClient.invalidateQueries({
        queryKey: ['users', 'spaceFilters']
      });
      return { success: true, type: 'removed' };
    }
  };

  // Read from the cache, never fetched here (whoever renders the space page
  // fetches it). A subscription, not getQueryData during render: that read
  // the cache once, so a space that arrived after the first render never
  // showed. On web no caller renders this value, so the server and the
  // hydrating client cannot disagree about it.
  const { data: space } = useQuery<SpaceType>({
    queryKey,
    queryFn: skipToken
  });

  return {
    queryKey,
    isOwner,
    isModerator,
    isMember,
    updateSpace,
    pinSpaceFactiii,
    updateSpaceSlug,
    addRule,
    editRule,
    joinSpaceUpdate,
    leaveSpaceUpdate,
    filterChangeHandler,
    space
  };
};

export default useSpace;
